import { useState } from 'react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { PayrollPipeline } from '../../components/PayrollPipeline';
import { useReconcile, useEscalations, useEmployees } from '../../hooks';
import { RefreshCw, CheckCircle } from 'lucide-react';
import type { Escalation } from '@payroll/shared';
import { useToast } from '../../components/ui/toast';
import { useConfirm } from '../../components/ui/confirm';
import { lastPeriods } from '../../lib/thai-date';

function getLast6Periods(): string[] {
  return lastPeriods(6);
}

interface ReconcileResult {
  processed: number;
  escalations_created: number;
}

const ESC_TYPE_LABEL: Record<string, string> = {
  short_work: 'ทำงานไม่ครบเวลา',
  late: 'เข้างานสาย',
  absent_no_evidence: 'ขาดงาน (ไม่มีหลักฐาน)',
  over_leave_quota: 'ลาเกินสิทธิ์',
  checkin_geofence_failed: 'เช็คอินนอกพื้นที่',
  conditional_evidence_overdue: 'เลยกำหนดส่งหลักฐาน',
  profile_incomplete: 'ประวัติไม่ครบ',
  missing_break_scan: 'ไม่สแกนพักเที่ยง',
  missing_clock_out: 'ไม่สแกนออกงาน',
  emergency_leave_review: 'ลาฉุกเฉินรอตัดสิน',
  no_return_followup: 'ไม่กลับมาทำงาน',
  ot_mismatch: 'OT ไม่ตรงเวลาจริง',
  ot_unrequested: 'OT ไม่ได้ขออนุมัติ',
  unscheduled_work: 'มาทำงานวันที่ไม่ได้กำหนด',
};
export const escTypeLabel = (t: string) => ESC_TYPE_LABEL[t] ?? t;

/** Content-only version used by the PayrollCycle hub. */
export function OwnerReconcileContent() {
  const [period, setPeriod] = useState<string>(getLast6Periods()[0] ?? '');
  const [result, setResult] = useState<ReconcileResult | null>(null);
  const reconcile = useReconcile();
  const { data: escalations } = useEscalations() as { data: Escalation[] | undefined };
  const { data: employees } = useEmployees() as { data: Array<{ emp_code: string; first_name: string; last_name: string; nickname?: string | null }> | undefined };
  const empName = new Map((employees ?? []).map((e) => [e.emp_code, `${e.first_name} ${e.last_name}`.trim() + (e.nickname ? ` (${e.nickname})` : '')]));
  const nameOf = (code: string) => empName.get(code) ?? code;
  const toast = useToast();
  const confirm = useConfirm();

  const pendingEsc = (escalations ?? []).filter((e) => e.status === 'pending');

  async function handleRun() {
    if (!(await confirm({ title: 'สรุปเวลาเข้างาน', message: `รันสรุปเวลางวด ${period}?` }))) return;
    try {
      const res = await reconcile.mutateAsync({ period });
      setResult(res as ReconcileResult);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'เกิดข้อผิดพลาด');
    }
  }

  return (
    <>
      <PayrollPipeline current="reconcile" period={period} />
      <div className="card p-4 mb-4">
        <label className="label">เลือกงวด</label>
        <select value={period} onChange={(e) => setPeriod(e.target.value)} className="input mb-3">
          {getLast6Periods().map((p) => (
            <option key={p} value={p}>{p}</option>
          ))}
        </select>
        <button onClick={handleRun} disabled={reconcile.isPending} className="btn-primary w-full">
          <RefreshCw className={`w-4 h-4 ${reconcile.isPending ? 'animate-spin' : ''}`} />
          {reconcile.isPending ? 'กำลังประมวลผล...' : 'เริ่มสรุปเวลา'}
        </button>
      </div>

      {result && (
        <div className="card p-4 bg-green-50 border-green-200 mb-4">
          <p className="font-medium text-green-800 mb-1 flex items-center gap-1.5">
            <CheckCircle className="w-4 h-4 text-[#2f7d52]" /> สรุปเวลาเสร็จแล้ว
          </p>
          <p className="text-sm text-green-700">ประมวลผลพนักงาน: {result.processed} คน</p>
          <p className="text-sm text-green-700">พบรายการผิดปกติใหม่: {result.escalations_created} รายการ</p>
        </div>
      )}

      {pendingEsc.length > 0 && (
        <div>
          <p className="text-sm font-semibold text-gray-700 mb-2">รายการที่ต้องตรวจสอบ ({pendingEsc.length})</p>
          <div className="space-y-2">
            {pendingEsc.map((e) => (
              <div key={e.esc_id} className="card p-3">
                <div className="flex justify-between">
                  <p className="font-medium text-sm">{nameOf(e.emp_code)}</p>
                  <span className="badge-warn">{escTypeLabel(e.type)}</span>
                </div>
                <p className="text-xs text-gray-600">{e.date}</p>
                {e.detail && <p className="text-xs text-gray-500 mt-1">{e.detail}</p>}
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

/** Standalone page kept for legacy /owner/reconcile (now redirects). */
export function OwnerReconcile() {
  return (
    <AdminLayout title="สรุปเวลาเข้างาน">
      <OwnerReconcileContent />
    </AdminLayout>
  );
}
